export abstract class RegexUtil {
    private static readonly NUMBER_REGEX: RegExp = /^\d*$/;
    private static readonly ALPHABET_REGEX: RegExp = /^[A-Za-z]+$/;
    private static readonly ALPHA_NUMERIC_REGEX: RegExp = /^[a-zA-Z0-9]*$/;
    private static readonly ALL_CAPS_ALPHA_NUMERIC_WITH_UNDERSCORES_REGEX: RegExp = /^[A-Z0-9_]+$/;
    private static readonly PASSWORD_REGEX: RegExp =
        /^((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!"#$%&'()*+,\-./:;\\<=>?@[\]^_`{|}~]).{8,32})$/;
    private static readonly WHITE_SPACE_REGEX: RegExp = /\s/;
    private static readonly IPV4_PATTERN_REGEX: RegExp = /^(\d{1,3}\.){3}\d{1,3}$/;
    private static readonly PORT_REGEX: RegExp = /^(\d{1,4}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5])$/;
    private static readonly IPV4_CIDR_REGEX: RegExp = /^(\d{1,3}\.){3}\d{1,3}(\/(\d|[1-2]\d|3[0-2]))$/;
    private static readonly FLOAT_NUMBER_REGEX: RegExp = /^[+-]?\d+(\.\d+)?$/;
    private static readonly FILE_PATH_REGEX: RegExp = /^\/([A-Za-z0-9-_+]+\/)*([A-Za-z0-9-_+]+)$/;
    private static readonly INVALID_FILE_NAME_REGEX: RegExp = /[/\\?%*|:"<>]/g;
    private static readonly EMAIL_LOCAL_REGEX: RegExp = /^[^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*$/;
    private static readonly EMAIL_DOMAIN_REGEX: RegExp = /^(\[\d{1,3}(\.\d{1,3}){3}\]|([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})$/;
    private static readonly HOSTNAME_REGEX: RegExp =
        /^((?!-)[a-zA-Z0-9-]{1,63}(?<!-)\.)+((?!-)[a-zA-Z0-9-]{1,63}(?<!-))$/;
    private static readonly MULTIPLE_ASTERISK_REGEX = /^(\*)\1+$/;
    private static readonly PARENT_TASK_FOLDER_REGEX = /\/Task #\d+(\.\d+)?,[^/]+$/;
    /** Endpoint agent target versions: major.minor.patch only (optional leading `v`), per endpoint-control API. */
    private static readonly AGENT_TARGET_VERSION_REGEX: RegExp = /^v?\d+\.\d+\.\d+$/;

    public static isValidEmail(value: string): boolean {
        const atIndex = value.lastIndexOf('@');
        if (atIndex <= 0 || atIndex === value.length - 1) {
            return false;
        }

        const local = value.slice(0, atIndex);
        const domain = value.slice(atIndex + 1);

        if (local.startsWith('"') && local.endsWith('"')) {
            return local.length > 2 && RegexUtil.EMAIL_DOMAIN_REGEX.test(domain);
        }

        return RegexUtil.EMAIL_LOCAL_REGEX.test(local) && RegexUtil.EMAIL_DOMAIN_REGEX.test(domain);
    }

    public static isNotValidEmail(value: string): boolean {
        return !RegexUtil.isValidEmail(value);
    }

    public static areEmailsValid(value: string): boolean {
        const emails = value.split(/[;,\s]+/).filter(Boolean);
        return emails.length > 0 && emails.every((email) => RegexUtil.isValidEmail(email));
    }

    public static areEmailsNotValid(value: string): boolean {
        return !RegexUtil.areEmailsValid(value);
    }

    public static isValidNumber(value: string): boolean {
        return RegexUtil.NUMBER_REGEX.test(value);
    }

    public static isNotValidNumber(value: string): boolean {
        return !RegexUtil.isValidNumber(value);
    }

    public static isValidAlphabet(value: string): boolean {
        return RegexUtil.ALPHABET_REGEX.test(value);
    }

    public static isNotValidAlphabet(value: string): boolean {
        return !RegexUtil.isValidAlphabet(value);
    }

    public static isValidAlphaNumeric(value: string): boolean {
        return RegexUtil.ALPHA_NUMERIC_REGEX.test(value);
    }

    public static isNotValidAlphaNumeric(value: string): boolean {
        return !RegexUtil.isValidAlphaNumeric(value);
    }

    public static isValidAllCapsAlphaNumericWithUnderscores(value: string): boolean {
        return RegexUtil.ALL_CAPS_ALPHA_NUMERIC_WITH_UNDERSCORES_REGEX.test(value);
    }

    public static isValidPassword(value: string): boolean {
        return RegexUtil.PASSWORD_REGEX.test(value) && !RegexUtil.WHITE_SPACE_REGEX.test(value);
    }

    public static isNotValidPassword(value: string): boolean {
        return !RegexUtil.isValidPassword(value);
    }

    public static isValidIP(value: string): boolean {
        return RegexUtil.isValidIPv4(value) || RegexUtil.isValidIPv6(value);
    }

    public static isNotValidIP(value: string): boolean {
        return !RegexUtil.isValidIP(value);
    }

    public static isValidPort(value: string): boolean {
        return RegexUtil.PORT_REGEX.test(value);
    }

    public static isNotValidPort(value: string): boolean {
        return !RegexUtil.isValidPort(value);
    }

    public static isValidCIDR(value: string): boolean {
        return RegexUtil.IPV4_CIDR_REGEX.test(value) || RegexUtil.isValidIPv6Cidr(value);
    }

    public static isNotValidCIDR(value: string): boolean {
        return !RegexUtil.isValidCIDR(value);
    }

    public static isValidRange(value: string): boolean {
        const ips = value.split('-');
        return RegexUtil.isValidIP(ips[0]) && RegexUtil.isValidIP(ips[1]);
    }

    public static isValidFloatNumber(value: string): boolean {
        return RegexUtil.FLOAT_NUMBER_REGEX.test(value);
    }

    public static isNotValidFloatNumber(value: string): boolean {
        return !RegexUtil.isValidFloatNumber(value);
    }

    public static isValidSingleQuoteStr(value: string): boolean {
        return value.startsWith("'") && value.endsWith("'") && value.length > 2;
    }

    public static isNotValidSingleQuoteStr(value: string): boolean {
        return !RegexUtil.isValidSingleQuoteStr(value);
    }

    public static isValidFilePath(value: string): boolean {
        return RegexUtil.FILE_PATH_REGEX.test(value);
    }

    public static isNotValidFilePath(value: string): boolean {
        return !RegexUtil.isValidFilePath(value);
    }

    public static isValidFolderName(value: string): boolean {
        return !value.includes('/') && !value.includes('\\');
    }

    public static isNotValidFolderName(value: string): boolean {
        return !RegexUtil.isValidFolderName(value);
    }

    public static getValidFileName(value: string, replaceValue: string): string {
        return value.replace(RegexUtil.INVALID_FILE_NAME_REGEX, replaceValue);
    }

    public static isValidHostname(value: string): boolean {
        return RegexUtil.HOSTNAME_REGEX.test(value);
    }

    public static isValidAuthProp(value: string): boolean {
        return !RegexUtil.MULTIPLE_ASTERISK_REGEX.test(value);
    }

    public static isValidParentTaskFolder(value: string): boolean {
        return RegexUtil.PARENT_TASK_FOLDER_REGEX.test(value);
    }

    /** Validates agent pin/default versions for save; two-segment strings are rejected intentionally (API is x.y.z). */
    public static isValidAgentTargetVersion(value: string): boolean {
        const trimmed = value.trim();
        return trimmed.length > 0 && RegexUtil.AGENT_TARGET_VERSION_REGEX.test(trimmed);
    }

    public static isNotValidAgentTargetVersion(value: string): boolean {
        return !RegexUtil.isValidAgentTargetVersion(value);
    }

    private static isValidIPv4(value: string): boolean {
        if (!RegexUtil.IPV4_PATTERN_REGEX.test(value)) {
            return false;
        }

        return value.split('.').every((octet) => {
            const num = Number(octet);
            return num >= 0 && num <= 255;
        });
    }

    private static isValidIPv6(value: string): boolean {
        if (!/^[0-9A-Fa-f:.]+$/.test(value) || value.split('::').length > 2) {
            return false;
        }

        const sections = value.includes('::') ? value.split('::') : [value, ''];
        const headGroups = sections[0] ? sections[0].split(':').filter(Boolean) : [];
        const tailGroups = sections[1] ? sections[1].split(':').filter(Boolean) : [];

        if (headGroups.some((group) => group.length > 4) || tailGroups.some((group) => group.length > 4)) {
            return false;
        }

        const groupCount = headGroups.length + tailGroups.length;
        if (value.includes('::')) {
            return groupCount <= 7;
        }

        return groupCount === 8;
    }

    private static isValidIPv6Cidr(value: string): boolean {
        const slashIndex = value.lastIndexOf('/');
        if (slashIndex <= 0) {
            return false;
        }

        const address = value.slice(0, slashIndex);
        const prefix = Number(value.slice(slashIndex + 1));

        return RegexUtil.isValidIPv6(address) && Number.isInteger(prefix) && prefix >= 0 && prefix <= 128;
    }
}
